import React from 'react';
import { Box, Button, Divider, Menu, MenuItem, Stack, Typography } from '@mui/material';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { dataAsOfDay } from '../dialing-opportunity/components/PeriodComparePicker';

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

const ISO = 'YYYY-MM-DD';

export interface ScorecardRange {
  start: string;
  end: string;
}

/**
 * Period picker for the Scorecard.
 *
 * Deliberately not the Dialing Opportunity picker: that one's presets are
 * day counts ("Last 30 days"), and the Scorecard's trend grain is derived
 * from whether the selection *is* a calendar week, month or quarter. A
 * "last 30 days" range is not a month, so it would always fall through to
 * the generic span grain and never show a monthly trend. These presets
 * are calendar-aligned so the grain the user expects is the one they get.
 *
 * Every preset ends no later than yesterday -- the warehouse loads a day
 * in arrears, so including today would show a partial day as a full one.
 * `dataAsOfDay` is shared with Dialing Opportunity so both products agree
 * on where "now" ends.
 */
interface PresetDef {
  id: string;
  label: string;
  /** What the trend will show, so the consequence of the choice is
   * visible before it is made rather than discovered afterwards. */
  trend: string;
  range: () => { start: Dayjs; end: Dayjs };
}

const PRESETS: PresetDef[] = [
  {
    id: 'this_week',
    label: 'This week to date',
    trend: 'same days, last 7 weeks',
    range: () => ({ start: dataAsOfDay().startOf('isoWeek'), end: dataAsOfDay() }),
  },
  {
    id: 'last_week',
    label: 'Last full week',
    trend: 'last 12 weeks',
    range: () => ({
      start: dataAsOfDay().startOf('isoWeek').subtract(1, 'week'),
      end: dataAsOfDay().startOf('isoWeek').subtract(1, 'day'),
    }),
  },
  {
    id: 'last_month',
    label: 'Last full month',
    trend: 'last 6 months',
    range: () => ({
      start: dataAsOfDay().startOf('month').subtract(1, 'month'),
      end: dataAsOfDay().startOf('month').subtract(1, 'day'),
    }),
  },
  {
    id: 'last_quarter',
    label: 'Last full quarter',
    trend: 'last 4 quarters',
    range: () => ({
      start: dataAsOfDay().startOf('quarter').subtract(1, 'quarter'),
      end: dataAsOfDay().startOf('quarter').subtract(1, 'day'),
    }),
  },
];

export function defaultScorecardRange(): ScorecardRange {
  const { start, end } = PRESETS[0].range();
  return { start: start.format(ISO), end: end.format(ISO) };
}

export default function ScorecardPeriodPicker({
  value,
  label,
  trendDescription,
  disabled,
  onChange,
}: {
  value: ScorecardRange;
  /** Server's name for the period ("July 2026", "Q2 2026", "3 days"). */
  label?: string;
  /** Server's description of the trend, shown so the two always agree. */
  trendDescription?: string;
  disabled?: boolean;
  onChange: (range: ScorecardRange) => void;
}) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [customStart, setCustomStart] = React.useState<Dayjs | null>(dayjs(value.start));
  const [customEnd, setCustomEnd] = React.useState<Dayjs | null>(dayjs(value.end));

  React.useEffect(() => {
    setCustomStart(dayjs(value.start));
    setCustomEnd(dayjs(value.end));
  }, [value.start, value.end]);

  const close = () => setAnchor(null);

  const applyPreset = (preset: PresetDef) => {
    const { start, end } = preset.range();
    onChange({ start: start.format(ISO), end: end.format(ISO) });
    close();
  };

  const customValid =
    !!customStart && !!customEnd && !customEnd.isBefore(customStart, 'day') && !customEnd.isAfter(dataAsOfDay(), 'day');

  const applyCustom = () => {
    if (!customValid || !customStart || !customEnd) return;
    onChange({ start: customStart.format(ISO), end: customEnd.format(ISO) });
    close();
  };

  const display = `${dayjs(value.start).format('D MMM')} – ${dayjs(value.end).format('D MMM YYYY')}`;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<CalendarMonthRounded sx={{ fontSize: 18 }} />}
        sx={{ textTransform: 'none', fontSize: 12.5, py: 0.5 }}
      >
        {label ? `${label} · ${display}` : display}
      </Button>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close} slotProps={{ paper: { sx: { width: 320 } } }}>
        {PRESETS.map((preset) => (
          <MenuItem key={preset.id} onClick={() => applyPreset(preset)} sx={{ py: 1 }}>
            <Stack>
              <Typography variant="body2">{preset.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                Trend: {preset.trend}
              </Typography>
            </Stack>
          </MenuItem>
        ))}

        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
            Custom range
          </Typography>
          {/* No app-level LocalizationProvider exists in this codebase, so
              each picker supplies its own adapter -- the same thing
              PeriodComparePicker does. Without it the DatePicker throws
              on mount rather than degrading. */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={1.25}>
              <DatePicker
                label="From"
                value={customStart}
                maxDate={dataAsOfDay()}
                onChange={setCustomStart}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label="To"
                value={customEnd}
                minDate={customStart ?? undefined}
                maxDate={dataAsOfDay()}
                onChange={setCustomEnd}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <Typography variant="caption" color="text.secondary">
                {trendDescription
                  ? `Currently showing: ${trendDescription.toLowerCase()}.`
                  : 'The trend matches the length of the range you pick.'}
              </Typography>
              <Button size="small" variant="contained" disabled={!customValid} onClick={applyCustom}>
                Apply
              </Button>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Menu>
    </>
  );
}
